<script setup lang="ts">
// Sankey / flow diagram. Author returns flat `{ source, target, value }`
// rows; the host pivots into ECharts' nodes/links shape using a
// prototype-safe dedupe table (Object.create(null)) per round-4
// security M-2/M-5.
//
// Cycle handling per `spec.onCycle` — see `pivotSankeyRows` for the
// full semantics. Briefly: every mode produces a renderable DAG.
// `"warn"` (default) rewrites back-edges as self-loops and fires
// `sankey_cycle_detected`; `"self-loop"` is identical render with a
// distinct diagnostic code; `"drop"` filters cycle edges out. The
// distinct diagnostic codes let host telemetry tell a default-fired
// cycle apart from an explicit author choice.

import { computed, watch } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { SankeyChartSpec } from "../sbi/protocol";
import { CHART_COLORS, COLORS, FONT_STACK_MONO } from "../theme.ts";
import {
  chartChrome,
  chartTextColor,
  resolveCssVar,
  tooltipDefaults,
} from "./echarts-base";
import { pivotSankeyRows } from "./insight-pivot";
import { sankeyLayout } from "./sankey-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: SankeyChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
  (e: "diagnostic", code: string, detail: Record<string, unknown>): void;
}>();

const pivot = computed(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  return pivotSankeyRows(
    rows,
    props.spec.source,
    props.spec.target,
    props.spec.value,
    props.spec.onCycle ?? "warn",
  );
});

watch(
  pivot,
  (p) => {
    if (p.cycleEdges > 0) {
      const code =
        (props.spec.onCycle ?? "warn") === "self-loop"
          ? "sankey_cycle_self_looped"
          : (props.spec.onCycle ?? "warn") === "drop"
            ? "sankey_cycle_dropped"
            : "sankey_cycle_detected";
      emit("diagnostic", code, { count: p.cycleEdges });
    }
  },
  { immediate: true },
);

// Geometry from the chart width — margins, node size, label widths,
// left-hand labels on terminal nodes, vertical when columns crowd (see
// sankey-layout.ts).
const layout = computed(() =>
  sankeyLayout(pivot.value, {
    width: ctx.value.width,
    print: ctx.value.print,
    authorHeight: props.spec.height,
  }),
);

const option = computed(() => ({
  ...chartChrome(ctx.value),
  tooltip: tooltipDefaults("item", ctx.value),
  series: [
    {
      type: "sankey" as const,
      orient: layout.value.orient,
      ...(layout.value.left !== undefined ? { left: layout.value.left, right: layout.value.right } : {}),
      ...(layout.value.nodeWidth !== undefined ? { nodeWidth: layout.value.nodeWidth } : {}),
      // Terminal nodes label on their left: on the right they ran off
      // the canvas once the default 20% right margin went.
      data: pivot.value.nodes.map((n) =>
        layout.value.sinks.has(n.name) ? { ...n, label: { position: "left" as const } } : n,
      ),
      links: pivot.value.links,
      emphasis: { focus: "adjacency" as const },
      lineStyle: {
        color: "gradient" as const,
        opacity: 0.5,
      },
      // ECharts' default sankey layout runs 32 iterations of the
      // node-ordering optimiser; bumping to 64 gives the solver more
      // chances to find a low-crossing arrangement for graphs with
      // multi-input/output nodes (the visible "noise" in busy
      // sankeys is layout crossings, not bad data).
      layoutIterations: 64,
      // Larger vertical gap between nodes in the same column so
      // adjacent ribbons separate cleanly instead of bleeding into
      // each other; the chart needs the extra vertical room — see
      // the `height` clamp below. Tighter on narrow charts.
      nodeGap: layout.value.nodeGap,
      // Node fill — embedder token first, canvas-safe hex fallback.
      // Without resolution, canvas paints the nodes with an invalid
      // colour and the diagram renders as floating labels with no
      // node rectangles.
      itemStyle: {
        color:
          resolveCssVar("var(--lucent-sankey-node-fill)") ??
          CHART_COLORS.sankeyNodeFill,
      },
      // Node labels — ECharts sankey defaults to `position: 'right'`
      // for every node, which on non-terminal nodes drops the label
      // straight on top of the outgoing flow ribbons (the ribbons
      // start at the node's right edge). The paper-coloured
      // `backgroundColor` plus padding turns each label into a chip
      // that occludes the ribbon behind it, so source and middle
      // labels stay legible against the green flows.
      label: {
        color: chartTextColor(),
        fontFamily: FONT_STACK_MONO,
        fontSize: 11,
        padding: [3, 6],
        backgroundColor: resolveCssVar("var(--paper)") ?? COLORS.background,
        borderRadius: 3,
        ...layout.value.label,
      },
    },
  ],
}));

// Sankey needs more vertical room than other chart types — every
// edge becomes a ribbon and ribbons share the column's height, so a
// 320px chart with 4 columns × 6 nodes squashes everything together
// (visible as flow ribbons stacking on top of each other rather than
// reading as discrete flows). Default 400 and max 600 give the
// ribbons room to breathe; min stays at 200 for the trivial case.
// A vertical (narrow) sankey stacks its columns down the page, so it
// grows with them instead.
const height = computed(() =>
  layout.value.orient === "vertical"
    ? resolveChartHeight("sankey", Math.max(400, layout.value.columns * 110))
    : resolveChartHeight("sankey", props.spec.height),
);

function onClick(p: { dataType?: string; data?: unknown }) {
  if (!props.spec.id || !p?.data) return;
  const datum = p.data as {
    name?: string;
    source?: string;
    target?: string;
    value?: number;
  };
  if (p.dataType === "edge") {
    emit("click-target", props.spec.id, {
      kind: "link",
      source: datum.source ?? "",
      target: datum.target ?? "",
      value: datum.value ?? 0,
    });
  } else {
    emit("click-target", props.spec.id, {
      kind: "node",
      name: datum.name ?? "",
    });
  }
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
